import { api } from './client';
import type {
  AnalyzeRequirementsResponse,
  Candidate,
  Interview,
  InterviewPublic,
  Interviewer,
  Job,
  RTCToken,
} from './types';

// ===== Jobs =====
export async function listJobs(): Promise<Job[]> {
  const { data } = await api.get<Job[]>('/api/jobs');
  return data;
}

export async function createJob(payload: {
  title: string;
  description: string;
  requirements?: string;
  capabilities?: any[];
  analysis_report?: any;
}): Promise<Job> {
  const { data } = await api.post<Job>('/api/jobs', payload);
  return data;
}

export async function getJob(id: string): Promise<Job> {
  const { data } = await api.get<Job>(`/api/jobs/${id}`);
  return data;
}

export async function analyzeRequirements(payload: {
  title: string;
  description?: string;
  charter_full?: string;     // 招聘简章原文(可选,大幅提升输出质量)
  boss_voice?: string;       // 老板原话(可选,触发 Stage 3 战略层分析)
}): Promise<AnalyzeRequirementsResponse> {
  // 深度分析 4 阶段并行,可能要 150-200s,给 5 分钟兜底
  const { data } = await api.post<AnalyzeRequirementsResponse>(
    '/api/jobs/analyze-requirements', payload, { timeout: 300_000 },
  );
  return data;
}

export async function uploadAndScreen(jobId: string, file: File, meta?: {
  name?: string; email?: string; phone?: string;
}): Promise<any> {
  const fd = new FormData();
  fd.append('job_id', jobId);
  fd.append('resume', file);
  if (meta?.name) fd.append('name', meta.name);
  if (meta?.email) fd.append('email', meta.email);
  if (meta?.phone) fd.append('phone', meta.phone);
  // 简历解析(60-90s) + 能力筛选(60-90s) 两次 LLM 调用,给 5 分钟兜底
  const { data } = await api.post('/api/screening/upload', fd, { timeout: 300_000 });
  return data;
}

export async function rerunScreening(jobId: string, candidateId: string): Promise<any> {
  const fd = new FormData();
  fd.append('job_id', jobId);
  fd.append('candidate_id', candidateId);
  const { data } = await api.post('/api/screening/rerun', fd, { timeout: 300_000 });
  return data;
}

export async function listScreeningsForJob(jobId: string): Promise<import('./types').ScreeningResult[]> {
  const { data } = await api.get<import('./types').ScreeningResult[]>(`/api/screening/job/${jobId}`);
  return data;
}

// ===== Candidates =====
export async function listCandidates(): Promise<Candidate[]> {
  const { data } = await api.get<Candidate[]>('/api/candidates');
  return data;
}

export async function createCandidate(payload: {
  name?: string;
  email?: string;
  phone?: string;
}): Promise<Candidate> {
  const { data } = await api.post<Candidate>('/api/candidates', payload);
  return data;
}

export async function uploadResume(file: File, meta?: { name?: string; email?: string }): Promise<Candidate> {
  const form = new FormData();
  form.append('resume', file);
  if (meta?.name) form.append('name', meta.name);
  if (meta?.email) form.append('email', meta.email);
  // 简历解析(60-90s) thinking 模型,给 3 分钟兜底
  const { data } = await api.post<Candidate>('/api/candidates/upload', form, { timeout: 180_000 });
  return data;
}

// ===== Interviews =====
export async function listInterviews(): Promise<Interview[]> {
  const { data } = await api.get<Interview[]>('/api/interviews');
  return data;
}

export async function createInterview(payload: {
  candidate_id: string;
  job_id: string;
  scheduled_at: string;
  duration_minutes?: number;
}): Promise<Interview> {
  // 后端改异步:创建立刻返回(< 1s),plan + question_pool 在后台生成
  const { data } = await api.post<Interview>('/api/interviews', payload);
  return data;
}

export async function cancelInterview(id: string): Promise<Interview> {
  const { data } = await api.post<Interview>(`/api/interviews/${id}/cancel`);
  return data;
}

export async function launchInterviewNow(id: string): Promise<Interview> {
  const { data } = await api.post<Interview>(`/api/interviews/${id}/launch`);
  return data;
}

export async function stopInterviewNow(id: string): Promise<Interview> {
  const { data } = await api.post<Interview>(`/api/interviews/${id}/stop`);
  return data;
}

// Candidate-side (by token)
export async function getInterviewByToken(token: string): Promise<InterviewPublic> {
  const { data } = await api.get<InterviewPublic>(`/api/interviews/by-token/${token}`);
  return data;
}

export async function issueRTCToken(token: string): Promise<RTCToken> {
  const { data } = await api.post<RTCToken>(`/api/interviews/by-token/${token}/rtc-token`);
  return data;
}

// ===== Interviewers (system-level config) =====
export async function listInterviewers(): Promise<Interviewer[]> {
  const { data } = await api.get<Interviewer[]>('/api/interviewers');
  return data;
}
export async function createInterviewer(payload: {
  name: string; avatar: string; voice: string; voice_label: string; description?: string;
}): Promise<Interviewer> {
  const { data } = await api.post<Interviewer>('/api/interviewers', payload);
  return data;
}
export async function updateInterviewer(
  id: string, payload: Partial<Pick<Interviewer, 'name' | 'avatar' | 'voice' | 'voice_label' | 'description'>>
): Promise<Interviewer> {
  const { data } = await api.patch<Interviewer>(`/api/interviewers/${id}`, payload);
  return data;
}
export async function setDefaultInterviewer(id: string): Promise<Interviewer> {
  const { data } = await api.post<Interviewer>(`/api/interviewers/${id}/set-default`);
  return data;
}
export async function deleteInterviewer(id: string): Promise<void> {
  await api.delete(`/api/interviewers/${id}`);
}
export async function uploadInterviewerAvatar(id: string, file: File): Promise<Interviewer> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<Interviewer>(`/api/interviewers/${id}/avatar`, fd);
  return data;
}
export async function resetInterviewerAvatar(id: string): Promise<Interviewer> {
  const { data } = await api.delete<Interviewer>(`/api/interviewers/${id}/avatar`);
  return data;
}
